/**
 * Tabs
 */
export class GMXTabs extends HTMLElement {
	selectedTextEl: HTMLElement
	tabListEl: HTMLUListElement
	tabBtns: NodeListOf<HTMLButtonElement>
	tabDropdownOverlayEl: HTMLElement

	private boundTabClickEventListener = this._handleTabClickHandler.bind(this)
	private boundTabDropdownClickHandler = this._handleTabDropdownClickHandler.bind(this)

	get selectedTabId(): string {
		return this.getAttribute('data-selected-tab-id') || ''
	}
	set selectedTabId(val: string) {
		this.setAttribute('data-selected-tab-id', val)
	}

	connectedCallback(): void {
		this.tabListEl = this.querySelector('ul') as HTMLUListElement
		this.tabBtns = this.tabListEl.querySelectorAll('button.tab-link-btn')

		if (this.classList.contains('dropdown-on-mobile')) {
			this.selectedTextEl = this.querySelector('.selected-dropdown-text')

			this.tabDropdownOverlayEl = document.createElement('gmx-tab-dropdown-overlay')
			this.tabDropdownOverlayEl.id = window.crypto.randomUUID()

			const dropdownContainerEl = document.createElement('div')
			dropdownContainerEl.classList.add('list-container')
			dropdownContainerEl.appendChild(this.tabListEl.cloneNode(true))
			this.tabDropdownOverlayEl.appendChild(dropdownContainerEl)

			document.body.appendChild(this.tabDropdownOverlayEl)

			this.tabDropdownOverlayEl.addEventListener('click', this.boundTabDropdownClickHandler)
		}

		// Activate the tab with the matching tab id
		this.setActiveTab(this.selectedTabId || this.tabBtns[0]?.getAttribute('aria-controls'))

		this.addEventListener('click', this.boundTabClickEventListener)
	}

	disconnectedCallback(): void {
		this.removeEventListener('click', this.boundTabClickEventListener)

		if (this.tabDropdownOverlayEl) {
			this.tabDropdownOverlayEl.removeEventListener('click', this.boundTabDropdownClickHandler)
			document.body.removeChild(this.tabDropdownOverlayEl)

			this.tabDropdownOverlayEl = undefined
		}
	}

	/**
	 * Handle dropdown click
	 * Applies to (mobile only)
	 * @param e {Event}
	 * @private
	 */
	private _handleTabDropdownClickHandler(e: Event): void {
		const target = e.target as HTMLElement

		const tabListEl = target.closest('.list-container')

		if (!tabListEl) {
			// Tab was not clicked, close dropdown
			this.classList.remove('gmx-tab-dropdown-open')
			this.tabDropdownOverlayEl.classList.remove('show')
			document.body.classList.remove('scroll-disabled')
		} else {
			this._handleTabClickHandler(e)
		}
	}

	/**
	 * Handle a click of somewhere in the tab container.  If tab was identified as clicked, activate the tab and
	 * reveal the associated content.
	 * @param e {Event}
	 * @private
	 */
	private _handleTabClickHandler(e: Event): void {
		const target = e.target as HTMLElement

		let selectedTarget: HTMLElement

		if (target.classList.contains('selected-dropdown-text')) {
			// This will only apply to mobile but if the selected-dropdown-text element is selected, this is the 'dropdown' element.
			this.classList.add('gmx-tab-dropdown-open')

			this.tabDropdownOverlayEl.classList.add('show')

			if (window.browserDetectSingleton) {
				window.browserDetectSingleton.setScrollbarWidthOffset() /* Ensure layout doesn't shift */
			}

			document.body.classList.add('scroll-disabled')
		} else {
			if (target.classList.contains('tab-link-btn')) {
				selectedTarget = target
			}

			if (selectedTarget && this.selectedTabId !== selectedTarget.getAttribute('aria-controls')) {
				this.setActiveTab(selectedTarget.getAttribute('aria-controls'))
			}

			this.classList.remove('gmx-tab-dropdown-open')

			if (this.tabDropdownOverlayEl && this.tabDropdownOverlayEl.classList.contains('show')) {
				this.tabDropdownOverlayEl.classList.remove('show')

				// If this tab instance is in an overlay, we don't want to remove the body scroll disabled as that
				// will be handled by the overlay itself
				if (!this.closest('gmx-overlay')) {
					document.body.classList.remove('scroll-disabled')
				}
			}
		}
	}

	/**
	 * Activate tab with given tab id
	 * @param tabId {string} - Tab id to activate
	 * @param noScrollTrigger {boolean} - If set, will pass along to the event that a scroll should NOT be triggered on tab change.  Typically this will be set when the caller has already set scroll position (ie gallery panel)
	 */
	setActiveTab(tabId: string, noScrollTrigger = false): void {
		if (this.selectedTabId !== tabId) {
			this.tabBtns.forEach(tab => {
				if (tab.getAttribute('aria-controls') === tabId) {
					tab.setAttribute('aria-selected', 'true')
					this.selectedTabId = tabId

					if (this.selectedTextEl) {
						this.selectedTextEl.innerText = tab.textContent
					}
				} else {
					tab.setAttribute('aria-selected', 'false')
				}
			})

			// Also need to loop through mobile dropdown options
			if (this.tabDropdownOverlayEl) {
				this.tabDropdownOverlayEl.querySelectorAll('button.tab-link-btn').forEach(tab => {
					if (tab.getAttribute('aria-controls') === tabId) {
						tab.setAttribute('aria-selected', 'true')
					} else {
						tab.setAttribute('aria-selected', 'false')
					}
				})
			}

			const newEvent = new CustomEvent('gmxTabValueChange', {
				bubbles: true,
				detail: {
					selectedItem: tabId,
					noScrollTrigger: noScrollTrigger,
				}
			})

			this.dispatchEvent(newEvent)
		}
	}
}

if (!customElements.get('gmx-tabs')) {
	customElements.define('gmx-tabs', GMXTabs)
}
